import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useMemo, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import ECharts from '~/components/echarts'
import AzureDashboardFilters from '~/dashboard/azure_dashboard_filters'
import SimpleTable from '~/dashboard/tables/simple_table'
import {
  azurePrevious18MonthsChartPath,
  azurePreviousMonthSpendByMeterCategoryChartPath,
  reservationsOrganizationAzureDashboardIndexPath,
  // azureDailySpendByMeterCategoryChartPath,
  // azureDailySpendByServiceFamilyChartPath
} from '~/routes'

export default function AzureDashboardCharts({ organizationId: orgId, defaultTab: defaultTabParam }) {
  const defaultTab = defaultTabParam || 'main-tab'
  const [mainChartParams, setMainChartParams] = useState({ azureCostKind: 'ActualCost' })
  // const [dailyChartParams, setDailyChartParams] = useState({ azureCostKind: 'ActualCost' })

  const [openedTabs, setOpenedTabs] = useState([defaultTab])
  const [activeTab, setActiveTab] = useState(defaultTab)

  const handleTabChange = (tabName) => {
    setActiveTab(tabName)

    // Lazy load charts
    if (!openedTabs.includes(tabName)) {
      setOpenedTabs((prevOpenedTabs) => [...prevOpenedTabs, tabName])
    }
  }

  const openedTab = (tabName) => {
    return openedTabs.includes(tabName)
  }

  // memoize URLs to prevent rerenders
  const previousMonthSpendByMeterCategoryUrl = useMemo(
    () => azurePreviousMonthSpendByMeterCategoryChartPath(orgId),
    [orgId],
  )
  const previous18MonthsUrl = useMemo(() => azurePrevious18MonthsChartPath(orgId), [orgId])
  // const azureDailySpendByMeterCategoryUrl = useMemo(() => azureDailySpendByMeterCategoryChartPath(orgId), [orgId])
  // const azureDailySpendByServiceFamilyUrl = useMemo(() => azureDailySpendByServiceFamilyChartPath(orgId), [orgId])
  const tableUrl = useMemo(() => reservationsOrganizationAzureDashboardIndexPath(orgId))

  const reservationColumns = [
    { key: 'reservation_name', label: t`Reservation Name`, link: true },
    { key: 'product_order_name', label: t`Product Order Name` },
    { key: 'cost_kind', label: t`Cost Kind` },
    { key: 'pricing_model', label: t`Pricing Model` },
    { key: 'cost_in_billing_currency', label: t`Cost` },
    { key: 'bucket', label: t`Bucket`, sortable: true },
  ]

  const reservationSarch = {
    fields: [{ name: 'reservation_name_cont', label: t`Reservation Name` }],
  }

  return (
    <div className='card-tabs'>
      <Tabs id='controlled-tabs' activeKey={activeTab} onSelect={(k) => handleTabChange(k)}>
        <Tab eventKey='main-tab' title={t`Main Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AzureDashboardFilters organizationId={orgId} setChartParams={setMainChartParams} />
                {openedTab('main-tab') && (
                  <>
                    <div className='row gy-4'>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Previous Month Spend By Meter Category`}
                          chartInfoTitle='azure_previous_month_spend_by_meter_category'
                          url={previousMonthSpendByMeterCategoryUrl}
                          params={mainChartParams}
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Previous 18 months spend by Meter Category`}
                          chartInfoTitle='azure_previous_18_months_spend_by_meter_category'
                          url={previous18MonthsUrl}
                          params={{ grouping_field: 'meter_category', ...mainChartParams }}
                          showExportButton
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Previous 18 months spend by Meter Sub Category`}
                          chartInfoTitle='azure_previous_18_months_spend_by_meter_sub_category'
                          url={previous18MonthsUrl}
                          params={{ grouping_field: 'meter_sub_category', ...mainChartParams }}
                          showExportButton
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Previous 18 months spend by Location`}
                          chartInfoTitle='azure_previous_18_months_spend_by_location'
                          url={previous18MonthsUrl}
                          params={{ grouping_field: 'resource_location', ...mainChartParams }}
                          showExportButton
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Previous 18 months spend by Subscription`}
                          chartInfoTitle='azure_previous_18_months_spend_by_subscription'
                          url={previous18MonthsUrl}
                          params={{ grouping_field: 'subscription_name', ...mainChartParams }}
                          showExportButton
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Previous 18 months spend by Pricing Model`}
                          chartInfoTitle='azure_previous_18_months_spend_by_pricing_model'
                          url={previous18MonthsUrl}
                          params={{ grouping_field: 'pricing_model', ...mainChartParams }}
                          showExportButton
                        />
                      </div>
                    </div>
                    <div className='card'>
                      <div className='card-body'>
                        <div className='footnotes'>
                          <p className='small'>
                            <Trans>
                              These charts apply to subscriptions with an Enterprise Agreement or a Microsoft Customer
                              Agreement.
                            </Trans>
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Tab>

        <Tab eventKey='reservation-tab' title={t`Reservation Spend`}>
          <div className='tab-content'>
            {openedTab('reservation-tab') && (
              <>
                <div className='row gy-4'>
                  <div className='col-sm-12'>
                    <SimpleTable
                      url={tableUrl}
                      context={{ columns: reservationColumns }}
                      polling={{ enabled: false, interval: 2 }}
                      search={reservationSarch}
                      exports={['csv']}
                    />
                  </div>
                </div>
                <div className='card tab-pane active show'>
                  <div className='card-footer'>
                    <p className='small mt-4'>
                      <Trans>This table shows data from the past two months.</Trans>
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </Tab>

        {/* <Tab eventKey='daily-tab' title='Daily Spend'>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AzureDashboardFilters organizationId={orgId} setChartParams={setDailyChartParams} />
                {openedTab('daily-tab') && (
                  <>
                    <div className='row gy-4'>
                      <div className='col-sm-6'>
                        <ECharts title='Previous 30 days spend by Meter Category *' chartInfoTitle='azure_previous_30_days_spend_by_meter_category' url={azureDailySpendByMeterCategoryUrl} params={dailyChartParams} showExportButton />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts title='Previous 30 days spend by Service Family *' chartInfoTitle='azure_previous_30_days_spend_by_service_family' url={azureDailySpendByServiceFamilyUrl} params={dailyChartParams} showExportButton />
                      </div>
                    </div>
                  </>
                )}
                <div className='row'>
                  <div className='col-12'>
                    <p className='small mt-4'>* To ensure uniformity across the charts, the data imported is consistently set to a three-day delay (D-3).</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Tab> */}
        {/* TODO: uncomment after fix the azure daily costs at ETL side */}
      </Tabs>
    </div>
  )
}
